import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from 'components/basicos';
import API, { getImagenUrl } from 'shared/api';
import { fmtMonto } from 'shared/format';
import { FiSearch, FiBox, FiCheck, FiX, FiMinus, FiPlus } from 'react-icons/fi';
import { kgToDisplay, displayToGramos } from './ProductoItem';
import BotonEscanear from 'shared/components/BotonEscanear';

function isDark() {
  try {
    return document.documentElement.getAttribute('data-dark') === 'true';
  } catch {
    return false;
  }
}

const LIMITE_CATALOGO = 40;
const LIMITE_BUSQUEDA = 50;

const cantBtn = {
  width: '38px',
  height: '38px',
  borderRadius: '8px',
  border: '1px solid var(--agx-border)',
  background: 'var(--agx-surface)',
  color: 'var(--agx-text)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const ProductoBuscadorModal = ({ open, onClose, sede, onAgregarProducto, productosActuales }) => {
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [cargandoCatalogo, setCargandoCatalogo] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [modoBusqueda, setModoBusqueda] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const ultimoCodigoRef = useRef(null);
  const enBusquedaRef = useRef(false);

  const [prodPrompt, setProdPrompt] = useState(null);
  const [cantTexto, setCantTexto] = useState('1');
  const [agregados, setAgregados] = useState([]);

  const codigosActuales = Array.from(
    new Set([
      ...(productosActuales || []).map((p) => p.codigo).filter(Boolean),
      ...agregados,
    ])
  );

  const cargarCatalogo = useCallback(
    async (reset) => {
      if (reset) {
        ultimoCodigoRef.current = null;
        setCargandoCatalogo(true);
      } else {
        setCargandoMas(true);
      }
      try {
        const params = new URLSearchParams({ sede: sede || '0101', limite: String(LIMITE_CATALOGO) });
        if (ultimoCodigoRef.current) params.set('ultimo_codigo', ultimoCodigoRef.current);
        const res = await API.get(`/pedidos/productos/buscar?${params.toString()}`);
        if (res.data.status === 'success') {
          const items = res.data.data || [];
          setCatalogo((prev) => (reset ? items : [...prev, ...items]));
          setHasMore(!!res.data.has_more);
          ultimoCodigoRef.current = res.data.ultimo_codigo || null;
        }
      } catch (e) {
        console.error('Error cargando catalogo:', e);
      }
      setCargandoCatalogo(false);
      setCargandoMas(false);
    },
    [sede]
  );

  const doSearch = useCallback(
    async (q) => {
      setBuscando(true);
      try {
        const res = await API.get(
          `/pedidos/productos/buscar?q=${encodeURIComponent(q)}&sede=${sede || '0101'}&limite=${LIMITE_BUSQUEDA}`
        );
        if (res.data.status === 'success') {
          setResultados(res.data.data || []);
        }
      } catch (e) {
        console.error('Error buscando:', e);
      }
      setBuscando(false);
    },
    [sede]
  );

  useEffect(() => {
    if (open) {
      setBusqueda('');
      setResultados([]);
      setModoBusqueda(false);
      enBusquedaRef.current = false;
      setCatalogo([]);
      setHasMore(false);
      setProdPrompt(null);
      setAgregados([]);
      cargarCatalogo(true);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, cargarCatalogo]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = busqueda.trim();
    if (q.length < 2) {
      debounceRef.current = setTimeout(() => {
        if (enBusquedaRef.current) {
          enBusquedaRef.current = false;
          setModoBusqueda(false);
          setResultados([]);
          cargarCatalogo(true);
        }
      }, 250);
    } else {
      debounceRef.current = setTimeout(() => {
        enBusquedaRef.current = true;
        setModoBusqueda(true);
        doSearch(q);
      }, 300);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [busqueda, open, doSearch, cargarCatalogo]);

  const handleAgregar = (prod) => {
    if (codigosActuales.includes(prod.codigo)) return;
    const pesable = prod.pesable || false;
    setProdPrompt(prod);
    setCantTexto(pesable ? '1,000' : '1');
  };

  const stockMaxPrompt = (() => {
    if (!prodPrompt) return 0;
    const stockReal = prodPrompt.stock ?? prodPrompt.n_cantidad ?? 0;
    return prodPrompt.pesable ? stockReal * 1000 : stockReal;
  })();

  const subirCantidad = () => {
    if (!prodPrompt) return;
    const pesable = prodPrompt.pesable || false;
    const step = pesable ? 250 : 1;
    const base = pesable ? (displayToGramos(cantTexto) ?? 1000) : (parseInt(cantTexto, 10) || 1);
    const limite = stockMaxPrompt > 0 ? stockMaxPrompt : Infinity;
    const n = Math.min(base + step, limite);
    setCantTexto(pesable ? kgToDisplay(n) : String(n));
  };

  const bajarCantidad = () => {
    if (!prodPrompt) return;
    const pesable = prodPrompt.pesable || false;
    const step = pesable ? 250 : 1;
    const base = pesable ? (displayToGramos(cantTexto) ?? 1000) : (parseInt(cantTexto, 10) || 1);
    const min = pesable ? 50 : 1;
    const n = Math.max(min, base - step);
    setCantTexto(pesable ? kgToDisplay(n) : String(n));
  };

  const confirmarCantidad = () => {
    if (!prodPrompt) return;
    const pesable = prodPrompt.pesable || false;
    const stockMax = stockMaxPrompt;
    let cantidad;
    if (pesable) {
      cantidad = displayToGramos(cantTexto);
      if (cantidad === null) return;
    } else {
      cantidad = parseInt(cantTexto, 10);
      if (isNaN(cantidad) || cantidad < 1) return;
    }
    if (stockMax > 0 && cantidad > stockMax) cantidad = stockMax;
    onAgregarProducto(prodPrompt, cantidad);
    setAgregados((prev) =>
      prev.includes(prodPrompt.codigo) ? prev : [...prev, prodPrompt.codigo]
    );
    setProdPrompt(null);
  };

  const lista = modoBusqueda ? resultados : catalogo;

  const renderCard = (p) => {
    const yaAgregado = codigosActuales.includes(p.codigo);
    const stockVal = p.stock ?? p.n_cantidad ?? 0;
    const imgSrc = getImagenUrl(p.thumb_url || p.imagen_url);
    const precio = typeof p.precio === 'number' ? p.precio : Number(p.precio || 0);
    const stockColor =
      stockVal > 10
        ? isDark()
          ? '#6ee7b7'
          : '#166534'
        : stockVal > 0
          ? isDark()
            ? '#fcd34d'
            : '#92400e'
          : isDark()
            ? '#fca5a5'
            : '#991b1b';
    const stockBg =
      stockVal > 10
        ? isDark()
          ? '#064e3b'
          : '#f0fdf4'
        : stockVal > 0
          ? isDark()
            ? '#78350f'
            : '#fffbeb'
          : isDark()
            ? '#7f1d1d'
            : '#fef2f2';

    return (
      <div
        key={p.codigo}
        onClick={() => !yaAgregado && handleAgregar(p)}
        title={yaAgregado ? 'Producto ya agregado' : 'Click para agregar'}
        style={{
          display: 'flex',
          flexDirection: 'column',
          border: yaAgregado ? '1px solid var(--agx-border)' : '1px solid var(--agx-border)',
          borderRadius: '12px',
          overflow: 'hidden',
          background: 'var(--agx-surface)',
          cursor: yaAgregado ? 'default' : 'pointer',
          opacity: yaAgregado ? 0.55 : 1,
          transition: 'transform 0.12s ease, box-shadow 0.12s ease',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={(e) => {
          if (yaAgregado) return;
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.10)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = '';
          e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.04)';
        }}
      >
        <div
          style={{
            width: '100%',
            aspectRatio: '1 / 1',
            background: 'var(--agx-bg2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={p.descripcion || p.codigo}
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <FiBox size={32} color="var(--agx-faint)" />
          )}
          {p.pesable && (
            <span
              style={{
                position: 'absolute',
                top: '6px',
                left: '6px',
                padding: '1px 6px',
                borderRadius: '4px',
                fontSize: '9px',
                fontWeight: 700,
                background: isDark() ? '#1e3a5f' : '#dbeafe',
                color: isDark() ? '#93c5fd' : '#1e40af',
              }}
            >
              Pesable
            </span>
          )}
          {yaAgregado && (
            <span
              style={{
                position: 'absolute',
                top: '6px',
                right: '6px',
                padding: '1px 6px',
                borderRadius: '4px',
                fontSize: '9px',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                background: isDark() ? '#14532d' : '#dcfce7',
                color: isDark() ? '#86efac' : '#166534',
              }}
            >
              <FiCheck size={10} /> Agregado
            </span>
          )}
        </div>
        <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
          <div
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--agx-text)',
              lineHeight: 1.25,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '30px',
            }}
          >
            {p.descripcion}
          </div>
          <div
            style={{
              fontSize: '10px',
              color: 'var(--agx-faint)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '4px',
            }}
          >
            <span style={{ fontWeight: 600 }}>{p.codigo}</span>
            {p.departamento && (
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '90px',
                }}
              >
                {p.departamento}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--agx-pri2)' }}>
              ${fmtMonto(precio)}
            </span>
            <span
              style={{
                padding: '0px 6px',
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: 700,
                background: stockBg,
                color: stockColor,
              }}
            >
              {stockVal > 0 ? `${stockVal}` : 'Agotado'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal open={open} onClose={onClose} title="Catálogo de productos" width="xl" noScroll>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 12px',
            border: '1px solid var(--agx-border)',
            borderRadius: '10px',
            background: 'var(--agx-input-bg)',
            flexShrink: 0,
          }}
        >
          <FiSearch size={16} color="var(--agx-muted)" />
          <input
            ref={inputRef}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por código, nombre, marca o modelo..."
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '14px',
              background: 'transparent',
              color: 'var(--agx-text)',
              fontFamily: 'inherit',
            }}
          />
          <BotonEscanear onScan={(codigo) => setBusqueda(codigo)} />
          {busqueda && (
            <button
              onClick={() => setBusqueda('')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--agx-muted)',
                padding: '2px',
                display: 'flex',
                borderRadius: '6px',
              }}
            >
              <FiX size={16} />
            </button>
          )}
        </div>

        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            marginTop: '10px',
            minHeight: 0,
          }}
        >
          {buscando && (
            <div style={{ textAlign: 'center', padding: '24px', fontSize: '13px', color: 'var(--agx-muted)' }}>
              Buscando...
            </div>
          )}

          {cargandoCatalogo && !modoBusqueda && (
            <div style={{ textAlign: 'center', padding: '24px', fontSize: '13px', color: 'var(--agx-muted)' }}>
              Cargando catálogo...
            </div>
          )}

          {!buscando && !cargandoCatalogo && modoBusqueda && lista.length === 0 && (
            <div style={{ textAlign: 'center', padding: '24px', fontSize: '13px', color: 'var(--agx-muted)' }}>
              Sin resultados para «{busqueda}»
            </div>
          )}

          {lista.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '12px',
                paddingBottom: '12px',
              }}
            >
              {lista.map(renderCard)}
            </div>
          )}

          {!modoBusqueda && hasMore && !cargandoCatalogo && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
              <button
                onClick={() => cargarCatalogo(false)}
                disabled={cargandoMas}
                style={{
                  padding: '8px 20px',
                  borderRadius: '8px',
                  border: '1px solid var(--agx-border)',
                  background: 'var(--agx-surface)',
                  color: 'var(--agx-pri2)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              >
                {cargandoMas ? 'Cargando...' : 'Cargar más'}
              </button>
            </div>
          )}
        </div>
      </div>

      {prodPrompt && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: '20px',
          }}
          onClick={() => setProdPrompt(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--agx-surface)',
              borderRadius: '14px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              width: '100%',
              maxWidth: '380px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
            }}
          >
            <div>
              <div style={{ fontSize: '12px', color: 'var(--agx-muted)', marginBottom: '4px' }}>
                {prodPrompt.codigo}
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--agx-text)', lineHeight: 1.3 }}>
                {prodPrompt.descripcion}
              </div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--agx-pri2)', marginTop: '4px' }}>
                ${fmtMonto(typeof prodPrompt.precio === 'number' ? prodPrompt.precio : Number(prodPrompt.precio || 0))}
                {prodPrompt.pesable ? ' / kg' : ' / unidad'}
              </div>
            </div>

            <div>
              <label
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--agx-text)',
                  display: 'block',
                  marginBottom: '6px',
                }}
              >
                Cantidad {prodPrompt.pesable ? '(kg)' : '(unidades)'}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button type="button" onClick={bajarCantidad} style={cantBtn}>
                  <FiMinus size={16} />
                </button>
                <input
                  autoFocus
                  value={cantTexto}
                  onChange={(e) => setCantTexto(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      confirmarCantidad();
                    }
                  }}
                  inputMode={prodPrompt.pesable ? 'decimal' : 'numeric'}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    border: '1px solid var(--agx-border)',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: 600,
                    textAlign: 'center',
                    outline: 'none',
                    background: 'var(--agx-input-bg)',
                    color: 'var(--agx-text)',
                    fontFamily: 'inherit',
                  }}
                />
                <button type="button" onClick={subirCantidad} style={cantBtn}>
                  <FiPlus size={16} />
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setProdPrompt(null)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid var(--agx-border)',
                  background: 'var(--agx-surface)',
                  color: 'var(--agx-text)',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarCantidad}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'var(--agx-pri2)',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Agregar
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ProductoBuscadorModal;
